/**
 * Dynamic Minimum Markup — customer-profile minimum-markup policy.
 * Targets the Job Ops API `/api/DynMinMarkup` endpoints. Responses are wrapped in the
 * standard result envelope; `DynMinMarkupService` unwraps `.data` to these DTOs.
 */

/** One cost-tier rule: over `costOverValue`, require at least `markupPercentage`. */
export interface DynMinMarkupTier {
  costOverValue: number;
  markupPercentage: number;
}

/** A customer's full policy — GET response and PUT request body. */
export interface DynMinMarkupPolicy {
  customerKey: string;
  markupPercentageForEmergency: number;
  markupPercentageForNonEmergency: number;
  overValuesWithMarkupPercentages: DynMinMarkupTier[];
}

/** One option to threshold-check (POST /check-threshold). */
export interface DynMinMarkupOptionCheck {
  optionKey: string;
  hasVendorEstimate: boolean;
  vendorGrandTotal: number;
  customerGrandTotal: number;
  vendorKey?: string | null;
}

/** Request body for POST /check-threshold. */
export interface DynMinMarkupThresholdRequest {
  jobKey: string;
  customerKey: string;
  options: DynMinMarkupOptionCheck[];
}

/** Per-option preview outcome. */
export interface DynMinMarkupOptionResult {
  optionKey: string;
  isWithinThreshold: boolean;
  currentCustomerGrandTotal: number;
  expectedCustomerGrandTotal: number;
  markupPercentageFromDbData: number | null;
  costBasis: number;
  isDne: boolean;
  explanationMessage: string;
  validationErrorMessage: string;
}

/** Response for POST /check-threshold (per-option preview; not a set-level gate). */
export interface DynMinMarkupThresholdResponse {
  options: DynMinMarkupOptionResult[];
}

/**
 * How the admin chose to proceed after seeing a `DynMinMarkupAdjustmentProposal` on a create/update
 * call. Resend the SAME create/update request with this set to actually persist — there is no separate
 * confirm endpoint. `ProceedAnyway` is reserved for the (not yet built) escalation-approval replay path;
 * the UI never sends it directly.
 */
export enum MarkupAdjustmentDecision {
  AcceptProposed = 1,
  ManualOverride = 2,
  ProceedAnyway = 3,
}

/**
 * A concrete, server-computed fix for an under-threshold customer estimate — V1's own auto-adjust steps
 * (a port of ProjectRCS `CustInvoiceAndEstimateMutator`), shown when a MANUAL edit drops the total below
 * the minimum. A freshly opened estimate/invoice is already adjusted silently by the server, so this
 * only appears after an edit. First, $10 is added to the unit price of the (first) materials line
 * (`materialLineAdjustments`, at most one entry); if that alone doesn't close the shortfall, half hours
 * are added to the (first) labor line until the total (admin fee included) meets the minimum
 * (`laborLineAdjustments`, at most one entry). Nothing is persisted until the admin accepts this or
 * overrides it — see `MarkupAdjustmentDecision`.
 */
export interface DynMinMarkupAdjustmentProposal {
  optionKey: string;
  shortfallAmount: number;
  /** The materials line whose unit price is raised by $10 first (V1: only the first one). Empty when there is no materials line. */
  materialLineAdjustments: DynMinMarkupMaterialLineAdjustment[];
  /** The labor line lengthened in half-hour steps (V1: only the main labor line). Empty when `noLaborLineAvailable` or when the materials step alone closed the shortfall. */
  laborLineAdjustments: DynMinMarkupLaborLineAdjustment[];
  resultingCustomerGrandTotal: number;
  /** True when no labor line exists to target (e.g. a DNE-only option, or every labor line has a $0 rate) — Accept must be disabled; the admin must edit a line manually instead. */
  noLaborLineAvailable: boolean;
  underlying: DynMinMarkupOptionResult;
}

/**
 * Which save the approver replays when a markup escalation is approved. `Create` carries the whole create request
 * (single vendor, or a combined multi-vendor estimate with its portion titles); `Update` an edit of a saved estimate.
 * `CreateMulti` is the older keys-only multi-vendor shape — kept for rows already stored that way, not sent by the UI.
 */
export type MarkupEscalationRequestKind = 'Create' | 'CreateMulti' | 'Update';

/**
 * POST api/v1/admin/estimate-markup-escalations — "Send For Approval" (V1's manager override): the estimate is NOT
 * saved; the original save request is stored and replayed verbatim if an approver approves it.
 */
export interface MarkupEscalationRequest {
  jobKey: string;
  customerKey: string;
  /** The estimate being edited; omitted when creating a new one. */
  customerEstimateKey?: string | null;
  vendorEstimateKey?: string | null;
  optionKey: string;
  requestKind: MarkupEscalationRequestKind;
  /** The admin's reason for why this below-minimum estimate should be allowed. */
  reason?: string | null;
  /** The threshold result that triggered it (`DynMinMarkupAdjustmentProposal.underlying`). */
  thresholdResult: DynMinMarkupOptionResult;
  originalCreateRequest?: unknown;
  originalCreateMultiVendorEstimateKeys?: string[] | null;
  originalUpdateRequest?: unknown;
}

export interface MarkupEscalationResult {
  escalationKey: string;
  status: string;
}

/** The materials line's +$10-a-unit step within a `DynMinMarkupAdjustmentProposal` (`markupPercentApplied` is the % it works out to). */
export interface DynMinMarkupMaterialLineAdjustment {
  lineItemKey: string;
  chargeTypeKey: string;
  currentAmount: number;
  markupPercentApplied: number;
  resultingAmount: number;
  /** `resultingAmount - currentAmount` — this line's contribution toward closing the shortfall. */
  amountAdded: number;
}

/** The labor line's half-hour steps within a `DynMinMarkupAdjustmentProposal` (`shareOfShortfall` is the dollars those hours add). */
export interface DynMinMarkupLaborLineAdjustment {
  lineItemKey: string;
  chargeTypeKey: string;
  currentHours: number;
  additionalHours: number;
  resultingHours: number;
  laborRate: number;
  /** The dollar amount this specific line is absorbing (an equal share of the total shortfall). */
  shareOfShortfall: number;
}
